import { useEffect, useRef, useCallback } from 'react';

interface PollingOptions {
  /**
   * Callback function to execute on each poll
   */
  callback: () => Promise<void> | void;
  /**
   * Polling interval in milliseconds
   */
  interval: number;
  /**
   * Whether to pause polling when tab is hidden
   * @default true
   */
  pauseOnHidden?: boolean;
  /**
   * Whether to enable exponential backoff on errors
   * @default true
   */
  enableBackoff?: boolean;
  /**
   * Maximum number of retry attempts before stopping
   * @default 3
   */
  maxRetries?: number;
  /**
   * Callback when max retries exceeded
   */
  onMaxRetriesExceeded?: () => void;
}

interface PollingState {
  timeoutId: ReturnType<typeof setTimeout> | null;
  isRunning: boolean;
  retryCount: number;
  currentInterval: number;
}

const BACKOFF_DELAYS = [5000, 10000, 20000]; // 5s, 10s, 20s

export function usePolling({
  callback,
  interval,
  pauseOnHidden = true,
  enableBackoff = true,
  maxRetries = 3,
  onMaxRetriesExceeded,
}: PollingOptions) {
  const stateRef = useRef<PollingState>({
    timeoutId: null,
    isRunning: false,
    retryCount: 0,
    currentInterval: interval,
  });

  const executeCallback = useCallback(async () => {
    try {
      await callback();

      // Reset retry count on success
      if (stateRef.current.retryCount > 0) {
        stateRef.current.retryCount = 0;
        stateRef.current.currentInterval = interval;
      }
    } catch (error) {
      console.error('[usePolling] Error executing callback:', error);

      if (enableBackoff) {
        stateRef.current.retryCount += 1;

        if (stateRef.current.retryCount >= maxRetries) {
          console.error('[usePolling] Max retries exceeded, stopping polling');
          stop();
          onMaxRetriesExceeded?.();
          return;
        }

        // Apply exponential backoff
        const backoffDelay =
          BACKOFF_DELAYS[Math.min(stateRef.current.retryCount - 1, BACKOFF_DELAYS.length - 1)];
        stateRef.current.currentInterval = backoffDelay;
        console.warn(
          `[usePolling] Retry ${stateRef.current.retryCount}/${maxRetries}, next attempt in ${backoffDelay}ms`
        );
      }
    }
  }, [callback, interval, enableBackoff, maxRetries, onMaxRetriesExceeded]);

  const scheduleNext = useCallback(() => {
    if (!stateRef.current.isRunning) return;

    // Check if tab is hidden and pause is enabled
    if (pauseOnHidden && document.hidden) {
      return;
    }

    stateRef.current.timeoutId = setTimeout(async () => {
      await executeCallback();
      scheduleNext();
    }, stateRef.current.currentInterval);
  }, [executeCallback, pauseOnHidden]);

  const start = useCallback(() => {
    if (stateRef.current.isRunning) return;

    stateRef.current.isRunning = true;
    stateRef.current.retryCount = 0;
    stateRef.current.currentInterval = interval;

    // Execute immediately on start
    executeCallback().then(() => {
      scheduleNext();
    });
  }, [interval, executeCallback, scheduleNext]);

  const stop = useCallback(() => {
    if (stateRef.current.timeoutId) {
      clearTimeout(stateRef.current.timeoutId);
      stateRef.current.timeoutId = null;
    }
    stateRef.current.isRunning = false;
    stateRef.current.retryCount = 0;
    stateRef.current.currentInterval = interval;
  }, [interval]);

  // Handle visibility change
  useEffect(() => {
    if (!pauseOnHidden) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause polling when tab is hidden
        if (stateRef.current.timeoutId) {
          clearTimeout(stateRef.current.timeoutId);
          stateRef.current.timeoutId = null;
        }
      } else {
        // Resume polling when tab becomes visible
        if (stateRef.current.isRunning) {
          scheduleNext();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pauseOnHidden, scheduleNext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stateRef.current.timeoutId) {
        clearTimeout(stateRef.current.timeoutId);
        stateRef.current.timeoutId = null;
      }
      stateRef.current.isRunning = false;
    };
  }, []);

  return {
    start,
    stop,
    isPolling: stateRef.current.isRunning,
    retryCount: stateRef.current.retryCount,
  };
}
